import type { ConnectionRef, Fwaas } from "@shared/components/fwaas/FwaasData";
import type { ConnectionType } from "@shared/components/fwaas/ConnectionsHelpers";
import type { HaMember } from "kontrolEdge/components/Shared/VmSpecsCard";
import type { HaSection } from "../overview/overview.types";
import type { Tunnels } from "../tunnels/tunnels";
import type { FirewallNat } from "../nat/nat.types";
import type { AttentionItem, AttentionNode, Severity } from "../attention/needsAttention";

/**
 * The service map of a Virtual Firewall (CTX-1209, approved design v5): the firewall
 * between two lanes — what reaches it from the internet on the left, the customer's
 * private network on the right. Pure: what is on the map and where, so the drawing and
 * its tests read the same model.
 */
export type Lane = "public" | "private";

export const LANE_LABEL: Record<Lane, string> = {
    public: "Internet-facing",
    private: "Private network",
};

/**
 * How loud a node's alert is (CTX-1526): a critical item turns it red, as the header's pill
 * does, and anything else amber. Items only to review mark no node.
 */
export type AlertLevel = Exclude<Severity, "review">;

export type NodeIcon = "internet" | "published" | "remote" | "site-to-site" | "fwint" | "cloud" | "other";

/** How the line to the firewall is drawn: a contracted service is solid, the rest dashed. */
export type EdgeStyle = "service" | "logical";

export interface MapNode {
    /** Stable across readings: `internet`, `published`, `remote`, `site-to-site`, `cloud`, `fwint:<id>`… */
    id: string;
    lane: Lane;
    icon: NodeIcon;
    name: string;
    /** One line under the name: what it is and how it is doing. */
    meta: string;
    /** The pill on its line: IPX, NAT ×3, IPsec, FWInt, CC. */
    edgeLabel: string;
    edgeStyle: EdgeStyle;
    /** The most serious item about this node; `null` when none is. */
    alert: AlertLevel | null;
    /** The connections it stands for (none for published apps and tunnels). */
    connections: ConnectionRef[];
    /** Their kind of service, for where "View connection" leads; `null` for no connection. */
    connectionType: ConnectionType | null;
}

/** The firewall, or a node, or a whole lane. `null`: nothing selected. */
export type MapSelection = "firewall" | `lane:${Lane}` | string;

export interface ServiceMapInput {
    service: Fwaas;
    tunnels: Tunnels | null;
    /** `undefined` while on its way, `null` when the firewall could not answer. */
    nat: FirewallNat | null | undefined;
    attention: AttentionItem[];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
const idOf = (ref: ConnectionRef) => ref.subscriptionId || ref.id;

/** "IPX · 13-8D2F40", or "IPX · 2 connections" when there are several. */
function connectionsMeta(tag: string, refs: ConnectionRef[]): string {
    return refs.length === 1 ? `${tag} · ${idOf(refs[0])}` : `${tag} · ${plural(refs.length, "connection")}`;
}

/** Under a datacenter, the interface's own id; otherwise what it rides on. */
function interfaceMeta(ref: ConnectionRef, tag: string): string {
    if (ref.connectedServiceDatacenter) return `${tag} ${idOf(ref)}`;
    return ref.connectedServiceSubId ? `Through ${ref.connectedServiceSubId}` : "Private interface";
}

/** Connections that are neither Internet, cloud nor an interface of ours, by the list's title. */
const OTHER_PRIVATE: { key: "port" | "datacenter" | "branch"; tag: string; name: string }[] = [
    { key: "port", tag: "Port", name: "Port" },
    { key: "datacenter", tag: "DCI", name: "Datacenter" },
    { key: "branch", tag: "Branch", name: "Branch" },
];

const refsOf = (service: Fwaas, key: keyof Fwaas["connections"]): ConnectionRef[] => {
    const value = service.connections?.[key];
    return Array.isArray(value) ? value : [];
};

function publicNodes({ service, tunnels, nat }: ServiceMapInput): Omit<MapNode, "alert">[] {
    const nodes: Omit<MapNode, "alert">[] = [];
    const internet = refsOf(service, "internet");
    if (internet.length > 0) {
        nodes.push({
            id: "internet",
            lane: "public",
            icon: "internet",
            name: "Internet",
            meta: connectionsMeta("IPX", internet),
            edgeLabel: "IPX",
            edgeStyle: "service",
            connections: internet,
            connectionType: "internet",
        });
    }
    const apps = nat?.items.length ?? 0;
    if (apps > 0) {
        nodes.push({
            id: "published",
            lane: "public",
            icon: "published",
            name: "Published apps",
            meta: plural(apps, "app"),
            edgeLabel: `NAT ×${apps}`,
            edgeStyle: "logical",
            connections: [],
            connectionType: null,
        });
    }
    const dialUps = (tunnels?.items ?? []).filter((t) => t.kind === "dial-up");
    if (dialUps.length > 0) {
        const users = dialUps.reduce((sum, t) => (t.kind === "dial-up" ? sum + t.connections : sum), 0);
        nodes.push({
            id: "remote",
            lane: "public",
            icon: "remote",
            name: "Remote access",
            meta: `${plural(users, "user")} connected`,
            edgeLabel: "IPsec",
            edgeStyle: "logical",
            connections: [],
            connectionType: null,
        });
    }
    return nodes;
}

function privateNodes({ service, tunnels }: ServiceMapInput): Omit<MapNode, "alert">[] {
    // Each interface is its own node, named after the datacenter its port lives in
    // (CTX-1210), with its own id under it: two interfaces can share a datacenter, as
    // Skandia's do. Without a datacenter, its id names it. There is no BGP per interface
    // to show: a FWInt is a static link to a port (José, 2026-10-16).
    const interfaces = [
        ...refsOf(service, "fwint").map((ref) => ({ ref, tag: "FWInt", type: "fwint" as const })),
        ...refsOf(service, "sdwanInt").map((ref) => ({ ref, tag: "SD-WAN Int", type: "sdwanInt" as const })),
    ].map(({ ref, tag, type }) => ({
        id: `fwint:${idOf(ref)}`,
        lane: "private" as const,
        icon: "fwint" as const,
        name: ref.connectedServiceDatacenter ?? `${tag} ${idOf(ref)}`,
        meta: interfaceMeta(ref, tag),
        edgeLabel: tag,
        edgeStyle: "service" as const,
        connections: [ref],
        connectionType: type,
    }));
    const nodes: Omit<MapNode, "alert">[] = [...interfaces];
    const cloud = refsOf(service, "cloudConnectivity");
    if (cloud.length > 0) {
        nodes.push({
            id: "cloud",
            lane: "private",
            icon: "cloud",
            name: "Cloud connectivity",
            meta: connectionsMeta("CC", cloud),
            edgeLabel: "CC",
            edgeStyle: "service",
            connections: cloud,
            connectionType: "cloudConnectivity",
        });
    }
    for (const other of OTHER_PRIVATE) {
        const refs = refsOf(service, other.key);
        if (refs.length === 0) continue;
        nodes.push({
            id: other.key,
            lane: "private",
            icon: "other",
            name: other.name,
            meta: connectionsMeta(other.tag, refs),
            edgeLabel: other.tag,
            edgeStyle: "service",
            connections: refs,
            connectionType: other.key,
        });
    }
    const siteToSite = (tunnels?.items ?? []).filter((t) => t.kind === "site-to-site");
    if (siteToSite.length > 0 && tunnels) {
        nodes.push({
            id: "site-to-site",
            lane: "private",
            icon: "site-to-site",
            name: "Site-to-site VPN",
            meta: `${tunnels.up} of ${tunnels.total} up`,
            edgeLabel: "IPsec",
            edgeStyle: "logical",
            connections: [],
            connectionType: null,
        });
    }
    return nodes;
}

/**
 * The node each item points at, with the most serious level it carries; on the firewall
 * when its node is not drawn. A critical item outranks any other on the same node.
 */
export function alertedNodes(attention: AttentionItem[], drawn: Set<string>): Map<string, AlertLevel> {
    const alerted = new Map<string, AlertLevel>();
    for (const item of attention) {
        if (item.severity === "review" || !item.node) continue;
        const node: AttentionNode = item.node;
        const key = drawn.has(node) ? node : "firewall";
        if (alerted.get(key) !== "critical") alerted.set(key, item.severity);
    }
    return alerted;
}

export interface ServiceMapModel {
    nodes: MapNode[];
    /** The firewall's own alert (HA, load, or an item whose node is not drawn); `null` for none. */
    firewallAlert: AlertLevel | null;
}

export function buildServiceMap(input: ServiceMapInput): ServiceMapModel {
    const drafts = [...publicNodes(input), ...privateNodes(input)];
    const alerted = alertedNodes(input.attention, new Set(drafts.map((n) => n.id)));
    return {
        nodes: drafts.map((node) => ({ ...node, alert: alerted.get(node.id) ?? null })),
        firewallAlert: alerted.get("firewall") ?? null,
    };
}

/** The ids of the nodes on the map, for the fallbacks that put the rest on the firewall. */
export const drawnIds = (nodes: MapNode[]) => new Set(nodes.map((n) => n.id));

/** What a selection lights up; `null` lights everything (nothing, or the firewall). */
export function litNodes(selection: MapSelection | null, nodes: MapNode[]): Set<string> | null {
    if (selection === null || selection === "firewall") return null;
    if (selection.startsWith("lane:")) {
        const lane = selection.slice("lane:".length);
        return new Set(nodes.filter((n) => n.lane === lane).map((n) => n.id));
    }
    return new Set([selection]);
}

/** The attention items the inspector of a selection explains — with the same fallback as the map's amber. */
export function attentionFor(selection: MapSelection, attention: AttentionItem[], nodes: MapNode[]): AttentionItem[] {
    const drawn = new Set(nodes.map((n) => n.id));
    return attention.filter((item) => {
        if (item.severity === "review" || !item.node) return false;
        const node = drawn.has(item.node) ? item.node : "firewall";
        return node === selection;
    });
}

export interface HaPairMember {
    /** "A" active, "P" passive: what the firewall's node draws. */
    letter: "A" | "P";
    role: "Active" | "Passive";
    up: boolean;
}

/**
 * The HA members as the map names them: by role, never by serial (CTX-953). In an
 * active-passive pair the primary is the active one; in active-active both are.
 */
export function haPair(ha: HaSection | null | undefined, members: HaMember[]): HaPairMember[] {
    if (!ha || ha.mode === "standalone") return [];
    const bothActive = ha.mode === "active-active";
    return [...members]
        .sort((a, b) => (a.role === b.role ? 0 : a.role === "primary" ? -1 : 1))
        .map((member) => {
            const active = bothActive || member.role === "primary";
            return { letter: active ? "A" : "P", role: active ? "Active" : "Passive", up: member.status === "up" };
        });
}

// ---------- Geometry ----------

/** The drawing's own units; the canvas scales them to its width. */
export const MAP_WIDTH = 960;
const LANE_TOP = 68;
const ROW_HEIGHT = 108;
const LANE_PAD = 32;
const LANE_X: Record<Lane, number> = { public: 165, private: 795 };
/** Where an edge's pill sits: just inside the lane, on the firewall's side. */
const PILL_X: Record<Lane, number> = { public: 316, private: 644 };
export const LANE_RECT: Record<Lane, { x: number; width: number }> = {
    public: { x: 30, width: 270 },
    private: { x: 660, width: 270 },
};
export const LANE_HEADER_Y = 38;

export interface Point {
    x: number;
    y: number;
}

export interface MapLayout {
    height: number;
    laneTop: number;
    laneHeight: number;
    firewall: Point;
    laneX: Record<Lane, number>;
    position: Record<string, Point>;
}

/** Each lane's nodes stacked and centred; the firewall in the middle of the lanes. */
export function layoutServiceMap(nodes: MapNode[]): MapLayout {
    const byLane: Record<Lane, MapNode[]> = {
        public: nodes.filter((n) => n.lane === "public"),
        private: nodes.filter((n) => n.lane === "private"),
    };
    const rows = Math.max(byLane.public.length, byLane.private.length, 1);
    const laneHeight = rows * ROW_HEIGHT + LANE_PAD;
    const firstRowY = LANE_TOP + LANE_PAD / 2 + ROW_HEIGHT / 2;
    const position: Record<string, Point> = {};
    (Object.keys(byLane) as Lane[]).forEach((lane) => {
        const list = byLane[lane];
        const offset = ((rows - list.length) * ROW_HEIGHT) / 2;
        list.forEach((node, i) => {
            position[node.id] = { x: LANE_X[lane], y: firstRowY + offset + i * ROW_HEIGHT };
        });
    });
    return {
        height: LANE_TOP + laneHeight + 16,
        laneTop: LANE_TOP,
        laneHeight,
        firewall: { x: MAP_WIDTH / 2, y: LANE_TOP + laneHeight / 2 },
        laneX: LANE_X,
        position,
    };
}

interface Curve {
    from: Point;
    c1: Point;
    c2: Point;
    to: Point;
}

/** The firewall to a node: leaves it level, arrives at the node level. */
export function edgeCurve(firewall: Point, node: Point): Curve {
    const dir = node.x < firewall.x ? -1 : 1;
    return {
        from: firewall,
        c1: { x: firewall.x + dir * 150, y: firewall.y },
        c2: { x: node.x - dir * 170, y: node.y },
        to: node,
    };
}

export const curvePath = ({ from, c1, c2, to }: Curve) =>
    `M${from.x} ${from.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${to.x} ${to.y}`;

const bezier = (t: number, a: number, b: number, c: number, d: number) =>
    (1 - t) ** 3 * a + 3 * (1 - t) ** 2 * t * b + 3 * (1 - t) * t ** 2 * c + t ** 3 * d;

/**
 * Where the curve crosses the lane's inner edge, for the line's pill. Solved on the
 * curve itself (x grows monotonically along it), not with the DOM's `getTotalLength`,
 * so it is the same in a browser and in a test.
 */
export function pillPoint(curve: Curve, lane: Lane): Point {
    const target = PILL_X[lane];
    const dir = curve.to.x < curve.from.x ? -1 : 1;
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) / 2;
        const x = bezier(mid, curve.from.x, curve.c1.x, curve.c2.x, curve.to.x);
        if ((x - target) * dir > 0) hi = mid;
        else lo = mid;
    }
    const t = (lo + hi) / 2;
    return {
        x: bezier(t, curve.from.x, curve.c1.x, curve.c2.x, curve.to.x),
        y: bezier(t, curve.from.y, curve.c1.y, curve.c2.y, curve.to.y),
    };
}
